/**
 * TypeScript projections of the carrier-facing PFP DTOs exposed by lohi-server.
 *
 * Source of truth for the wire shape:
 *   lohi-server/src/product/energy/preferredPartner/types.go
 *
 * Keep these interfaces in lock-step with the Go DTOs. If the backend adds a
 * field, mirror it here with the same camelCase name and the same nullability
 * (optional or `null`-bearing).
 */

export type BonusType = 'positional' | 'floor_topup';

/**
 * Reason strings the eligibility evaluator persists into details.reason.
 * Mirrors agnus-server/preferredPartner/eligibility/carrier.go (lines 79-86)
 * plus the `region_not_in_program` sentinel that lohi-server stamps for
 * regions outside the allowlist.
 */
export const CARRIER_REASONS = {
  PASSED_WEEKEND_HITCH: 'passed_weekend_hitch_rule',
  PASSED_DAILY_CAPACITY: 'passed_daily_capacity_rule',
  PASSED_BOTH: 'passed_both_rules',
  FAILED_BOTH: 'failed_both_rules',
  NO_ROSTER: 'no_active_roster_in_region',
  WEEKEND_ONLY: 'weekend_only_carrier',
  REGION_NOT_IN_PROGRAM: 'region_not_in_program',
  OVERRIDE_NO_ORIGINAL: 'override_without_original_details',
  OVERRIDE_UNPARSEABLE: 'override_with_unparseable_original_details',
  OVERRIDE_CHAIN_TOO_DEEP: 'override_chain_exceeded_max_depth',
} as const;

export const DRIVER_RULES = {
  NO_LOAD_REJECTIONS: 'noLoadRejections',
  NO_MISSED_ACCEPTANCES: 'noMissedAcceptances',
  GPS_ALWAYS_ON: 'gpsAlwaysOn',
  NO_LATE_ARRIVALS: 'noLateArrivals',
} as const;

export type DriverRuleKey = typeof DRIVER_RULES[keyof typeof DRIVER_RULES];

// ===== Region + program config =====

export interface LoadTier {
  loadPosition: number;
  rateBasisPoints: number;
}

export interface ProgramConfig {
  id: number;
  name: string;
  shiftFloorCents: number;
  loadTiers: LoadTier[];
  evaluatorVersion: string;
}

export interface RegionProgramConfig {
  regionId: number;
  regionName: string;
  timezone?: string;
  inAllowlist: boolean;
  programConfig: ProgramConfig | null;
}

export interface ProgramConfigResponse {
  regions: RegionProgramConfig[];
}

// ===== Eligibility =====

export interface WeekendHitchRule {
  totalDrivers: number;
  driversWithWeekendHitch: number;
  thresholdRequired: number;
  smallRosterCap: number;
  requiredPercent: number;
  ruleApplied: string;
  passed: boolean;
}

export interface DailyCapacityRule {
  dailyCounts: Record<string, number>;
  maxWeekday: number;
  maxWeekend: number;
  ratio: number;
  requiredRatio: number;
  passed: boolean;
}

export interface EligibilityBreakdowns {
  evaluatorVersion: string;
  reason: string;
  weekendHitchRule: WeekendHitchRule | null;
  dailyCapacityRule: DailyCapacityRule | null;
  previousMonday?: string;
  previousFriday?: string;
  previousSunday?: string;
  regionTimezone?: string;
}

export interface EligibilityOverride {
  overriddenAt: string;
  overriddenBy: string;
  previousEligibilityId: number;
  previousCarrierEligible: boolean;
}

export interface EligibilityWeek {
  id: number;
  weekStart: string;
  regionId: number;
  regionName: string;
  programConfigId: number;
  carrierEligible: boolean;
  effectiveBreakdowns: EligibilityBreakdowns;
  override: EligibilityOverride | null;
  createdAt: string;
}

export interface EligibilityResponse {
  weeks: EligibilityWeek[];
}

// ===== Driver rules / shift evaluation =====

export interface RuleResult {
  passed: boolean;
  // Violation shape varies per rule; render defensively.
  violations: any[];
}

export interface DriverRulesCheck {
  evaluatorVersion: string;
  noLoadRejections: RuleResult;
  noMissedAcceptances: RuleResult;
  gpsAlwaysOn: RuleResult;
  noLateArrivals: RuleResult;
}

export interface ShiftEvaluation {
  id: number;
  shiftId: number;
  driverId: number;
  driverName: string;
  regionId: number;
  regionName: string;
  timezone?: string;
  programConfigId: number;
  eligibilityWeekStart: string;
  driverEligibleAtClose: boolean;
  floorEvaluated: boolean;
  floorApplied: boolean;
  shiftStart: string;
  shiftEnd: string | null;
  rulesCheck: DriverRulesCheck;
  createdAt: string;
}

export interface ShiftEvaluationsResponse {
  shiftEvaluations: ShiftEvaluation[];
  total: number;
  limit: number;
  offset: number;
}

// ===== Load bonuses =====

export interface LoadBonus {
  id: number;
  createdAt: string;
  orderId: number;
  shiftId: number;
  driverId: number;
  driverName: string;
  regionId: number;
  regionName: string;
  timezone?: string;
  programConfigId: number;
  bonusType: BonusType;
  loadPosition: number;
  bonusCents: number;
  archivedAt: string | null;
  archivedReason: string | null;
}

export interface LoadBonusTotals {
  positionalCents: number;
  floorTopupCents: number;
  archivedCents: number;
  netActiveCents: number;
}

export interface LoadBonusesResponse {
  loadBonuses: LoadBonus[];
  totals: LoadBonusTotals;
  total: number;
  limit: number;
  offset: number;
}

// ===== Summary =====

export interface SummaryRegion {
  regionId: number;
  regionName: string;
  timezone?: string;
  inAllowlist: boolean;
  carrierEligible: boolean | null;
  reason: string;
  shiftsEvaluated: number;
  driversEligibleAtClose: number;
  totalBonusCents: number;
}

export interface SummaryTotals {
  shiftsEvaluated: number;
  driversEligibleAtClose: number;
  totalBonusCents: number;
}

export interface SummaryLastWeek {
  weekStart: string;
  totalBonusCents: number;
}

export interface SummaryResponse {
  weekStart: string;
  totals: SummaryTotals;
  byRegion: SummaryRegion[];
  lastWeek: SummaryLastWeek;
}

// ===== Calendar (server-side source of truth for the current PFP week) =====

export interface CurrentWeekResponse {
  weekStart: string;
  weekEnd: string;
  serverTimeUTC: string;
}

// ===== Query option bags (typed wrappers used by the service) =====

export interface ListEligibilityOpts {
  weeks?: number;
  regionId?: number;
  weekStart?: string;
}

export interface ListShiftEvaluationsOpts {
  weekStart: string;
  driverEligibleAtClose?: boolean;
  regionId?: number;
  limit?: number;
  offset?: number;
}

export interface ListLoadBonusesOpts {
  weekStart?: string;
  bonusType?: BonusType;
  includeArchived?: boolean;
  driverId?: number;
  regionId?: number;
  limit?: number;
  offset?: number;
}

export interface SummaryOpts {
  weekStart?: string;
  regionId?: number;
}
